<template>
  <header class="enterprise-header">
    <div class="enterprise-shell enterprise-header__inner">
      <NuxtLink :to="localePath('/')" class="enterprise-brand" :aria-label="t('enterprise_home_header_home_aria')">
        <img :src="logo" alt="Doclingo" class="enterprise-brand__logo">
        <span class="enterprise-brand__badge">{{ t('enterprise_home_business') }}</span>
      </NuxtLink>

      <nav class="enterprise-header__nav" :aria-label="t('enterprise_home_header_nav_aria')">
        <button
          v-for="item in navigation"
          :key="item.id"
          type="button"
          :class="{ active: activeSection === item.id }"
          :aria-current="activeSection === item.id ? 'location' : undefined"
          @click="scrollToSection(item.id)"
        >
          {{ item.label }}
        </button>
      </nav>

      <div class="enterprise-header__actions">
        <ClientOnly>
          <el-popover
            v-if="isAiSite"
            ref="languagePopoverRef"
            placement="bottom-end"
            trigger="click"
            :width="240"
            popper-class="enterprise-language-popover"
          >
            <template #reference>
              <button class="enterprise-header__language" type="button">
                <span class="iconfont icon-icon_earth_1" />
                {{ systemLanguage?.name || t('enterprise_home_language_default') }}
              </button>
            </template>
            <div class="enterprise-language-list">
              <button
                v-for="item in languageList"
                :key="item.id"
                type="button"
                @click="changeLanguage(item)"
              >
                {{ item.name }}
              </button>
            </div>
          </el-popover>

          <a :href="personalSiteUrl" class="enterprise-header__personal">{{ t('enterprise_home_personal') }}</a>
          <button v-if="!token" type="button" class="enterprise-header__login" @click="showLogin">{{ t('enterprise_home_login') }}</button>
          <UserPopover v-else />
        </ClientOnly>

        <button class="enterprise-header__menu" type="button" :aria-label="t('enterprise_home_header_open_nav')" @click="menuOpen = !menuOpen">
          <span />
          <span />
          <span />
        </button>
      </div>
    </div>

    <div v-if="menuOpen" class="enterprise-header__mobile-nav">
      <button
        v-for="item in navigation"
        :key="item.id"
        type="button"
        :class="{ active: activeSection === item.id }"
        :aria-current="activeSection === item.id ? 'location' : undefined"
        @click="scrollToSection(item.id)"
      >
        {{ item.label }}
      </button>
      <a :href="personalSiteUrl">{{ t('enterprise_home_personal') }}</a>
    </div>
  </header>
</template>

<script setup lang="ts">
import type { SystemLanguageType } from '~/store/modules/systemLaunage'
import useSystemLanguageStore from '~/store/modules/systemLaunage'
import useUserStore from '~/store/modules/user'
import { useCrossDomainCookie } from '~/composables/useCrossDomainCookie'
import { useSiteConfig } from '~/config/useSiteConfig'
import type { SystemLanguageEnum } from '~/types/const'
import UserPopover from '~/components/LayoutHeader/UserPopover/index.vue'
import logo from '~/assets/images/icon_logo.png'

const localePath = useLocalePath()
const { locale, setLocale, t } = useI18n()
const { $loginCom } = useNuxtApp()
const { createCrossDomainCookie } = useCrossDomainCookie()
const { isAiSite } = useSiteConfig()
const { doclingoUrl } = useAppDomainConfig()
const userStore = useUserStore()
const { token } = storeToRefs(userStore)
const languageStore = useSystemLanguageStore()
const { languageList, systemLanguage } = storeToRefs(languageStore)
const activeSection = ref('product-view')
const menuOpen = ref(false)
const languagePopoverRef = ref()
let scrollContainer: HTMLElement | null = null
let scrollFrame: number | null = null

// 「个人版」跳转到同区域的个人站点并保留当前语言，如 biz.doclingo.ai/zh → doclingo.ai/zh
const personalSiteUrl = computed(() => `${doclingoUrl.value.replace(/\/+$/, '')}/${locale.value}`)

const navigation = computed(() => [
  { id: 'product-view', label: t('enterprise_home_nav_features') },
  { id: 'pro-view', label: t('enterprise_home_nav_pricing') },
  { id: 'rights-view', label: t('enterprise_home_nav_comparison') },
  { id: 'case-view', label: t('enterprise_home_nav_cases') },
  { id: 'faq-view', label: t('enterprise_home_nav_faq') },
])

function scrollToSection(id: string) {
  menuOpen.value = false
  const section = document.getElementById(id)
  if (!section) return
  activeSection.value = id
  section.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function changeLanguage(item: SystemLanguageType) {
  setLocale(item.code as SystemLanguageEnum)
  languageStore.setLanguage(item.code as SystemLanguageEnum)
  createCrossDomainCookie('lang', item.code)
  languagePopoverRef.value?.hide()
}

function showLogin() {
  $loginCom.open()
}

function updateActiveSection() {
  const sections = navigation.value
    .map(item => ({ ...item, element: document.getElementById(item.id) }))
    .filter((item): item is { id: string; label: string; element: HTMLElement } => item.element instanceof HTMLElement)
  if (!sections.length) {
    activeSection.value = ''
    return
  }

  if (scrollContainer
    && scrollContainer.scrollTop + scrollContainer.clientHeight >= scrollContainer.scrollHeight - 2) {
    activeSection.value = sections[sections.length - 1].id
    return
  }

  const activationLine = (scrollContainer?.getBoundingClientRect().top || 0) + 96
  let current = sections[0].id
  for (const item of sections) {
    if (item.element.getBoundingClientRect().top <= activationLine) {
      current = item.id
    }
  }
  activeSection.value = current
}

function handleScroll() {
  if (scrollFrame !== null) return
  scrollFrame = window.requestAnimationFrame(() => {
    scrollFrame = null
    updateActiveSection()
  })
}

onMounted(() => {
  scrollContainer = document.getElementById('enterprise-layout-content')
  scrollContainer?.addEventListener('scroll', handleScroll, { passive: true })
  window.addEventListener('resize', handleScroll, { passive: true })
  updateActiveSection()
})

onBeforeUnmount(() => {
  scrollContainer?.removeEventListener('scroll', handleScroll)
  window.removeEventListener('resize', handleScroll)
  if (scrollFrame !== null) window.cancelAnimationFrame(scrollFrame)
  scrollFrame = null
  scrollContainer = null
})
</script>
